/**
 * Preisstand und Abweichung.
 *
 * Zwischen dem Übernehmen des Warenkorbs und dem Absenden liegen Minuten bis Tage. In dieser
 * Zeit ändern sich Preise, Verfügbarkeiten, Lieferzeiten und Versandkosten. Diese Datei
 * beantwortet die eine Frage, die davor steht: hat sich etwas geändert, und ist das so
 * wesentlich, dass der Besteller es noch einmal sehen und bestätigen muss?
 *
 * Reine Rechnung, keine Ein-/Ausgabe -- damit Demo-Gateway, Adapter und Oberfläche zu
 * demselben Ergebnis kommen.
 */

import { partsAvailabilityRank, type PartsAvailability } from './types';

/**
 * Ab wann eine Änderung „wesentlich" ist.
 *
 * FORQ hatte dafür bisher keine Regel. Die Vorgabe hier ist bewusst streng gesetzt und je
 * Lieferantenanbindung einstellbar (`supplier_connections`):
 *
 *   percent  2   -- 2 % Preisänderung auf einer Position oder auf der Summe
 *   absolute 5   -- oder 5 € absolut, je nachdem, was zuerst greift. Ohne den absoluten
 *                   Betrag würden 2 % auf einen 4-€-Dichtring jeden Cent zur Rückfrage
 *                   machen; ohne den Prozentsatz gingen 4 € auf ein 40-€-Teil durch.
 *   leadTime 3   -- drei Tage mehr Lieferzeit sind für die Werkstattplanung ein anderer Tag.
 *
 * Versandkosten und eine schlechtere Verfügbarkeit gelten IMMER als wesentlich: beides ändert
 * die Entscheidung, ob man überhaupt bei diesem Lieferanten bestellt.
 */
export interface PriceDeviationRule {
  readonly percent: number;
  readonly absolute: number;
  readonly leadTimeDays: number;
}

export const defaultPriceDeviationRule: PriceDeviationRule = {
  percent: 2,
  absolute: 5,
  leadTimeDays: 3,
};

export type PriceDeviationKind =
  | 'unit-price'
  | 'availability'
  | 'lead-time'
  | 'shipping'
  | 'line-missing'
  | 'total';

/** Eine einzelne Abweichung -- alt und neu nebeneinander, damit die Oberfläche beides zeigen kann. */
export interface PriceDeviation {
  readonly kind: PriceDeviationKind;
  /** Betroffene Bestellposition oder null, wenn es die ganze Bestellung betrifft. */
  readonly lineId: string | null;
  readonly label: string;
  readonly previousValue: string;
  readonly currentValue: string;
  /** Wesentlich = erneute Bestätigung nötig. Unwesentliches wird trotzdem angezeigt, aber blockiert nicht. */
  readonly material: boolean;
}

export interface PriceSnapshotLine {
  readonly lineId: string;
  readonly label: string;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly availability: PartsAvailability;
  readonly leadTimeDays: number | null;
}

/** Preis, Verfügbarkeit und Versand zu einem Zeitpunkt -- einmal beim Übernehmen, einmal vor dem Absenden. */
export interface PriceSnapshot {
  readonly lines: readonly PriceSnapshotLine[];
  readonly shippingCost: number;
  readonly currency: string;
}

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function days(value: number | null): string {
  return value == null ? 'unbekannt' : `${value} Tage`;
}

/** Greift die Toleranz? Prozent UND Betrag müssen überschritten sein, damit Kleinteile nicht dauernd stören. */
function exceedsTolerance(previous: number, current: number, rule: PriceDeviationRule): boolean {
  const difference = Math.abs(current - previous);
  if (difference === 0) return false;
  const percent = previous === 0 ? Number.POSITIVE_INFINITY : (difference / previous) * 100;
  return percent > rule.percent && difference > rule.absolute;
}

/**
 * Was hat sich zwischen zwei Preisständen geändert?
 *
 * Positionen werden über `lineId` zugeordnet. Eine Position, die der Lieferant im zweiten Stand
 * nicht mehr kennt, ist `line-missing` und immer wesentlich -- sie stillschweigend mitzubestellen
 * wäre der schlimmste Ausgang.
 */
export function calculatePriceDeviations(
  previous: PriceSnapshot,
  current: PriceSnapshot,
  rule: PriceDeviationRule = defaultPriceDeviationRule,
): readonly PriceDeviation[] {
  const deviations: PriceDeviation[] = [];
  const currentLines = new Map(current.lines.map((line) => [line.lineId, line]));

  for (const before of previous.lines) {
    const after = currentLines.get(before.lineId);

    if (!after) {
      deviations.push({
        kind: 'line-missing',
        lineId: before.lineId,
        label: before.label,
        previousValue: `${before.quantity} × ${money(before.unitPrice)} ${previous.currency}`,
        currentValue: 'Vom Lieferanten nicht mehr geführt',
        material: true,
      });
      continue;
    }

    if (before.unitPrice !== after.unitPrice) {
      deviations.push({
        kind: 'unit-price',
        lineId: before.lineId,
        label: before.label,
        previousValue: `${money(before.unitPrice)} ${previous.currency}`,
        currentValue: `${money(after.unitPrice)} ${current.currency}`,
        material: exceedsTolerance(before.unitPrice, after.unitPrice, rule),
      });
    }

    if (before.availability !== after.availability) {
      deviations.push({
        kind: 'availability',
        lineId: before.lineId,
        label: before.label,
        previousValue: before.availability,
        currentValue: after.availability,
        // Nur die Verschlechterung blockiert. Wer schneller liefern kann als gedacht,
        // muss dafür nicht um Erlaubnis fragen.
        material: partsAvailabilityRank[after.availability] > partsAvailabilityRank[before.availability],
      });
    }

    const beforeLead = before.leadTimeDays;
    const afterLead = after.leadTimeDays;
    if (beforeLead !== afterLead) {
      const grewBy = afterLead == null || beforeLead == null ? null : afterLead - beforeLead;
      deviations.push({
        kind: 'lead-time',
        lineId: before.lineId,
        label: before.label,
        previousValue: days(beforeLead),
        currentValue: days(afterLead),
        material: grewBy == null ? true : grewBy > rule.leadTimeDays,
      });
    }
  }

  if (previous.shippingCost !== current.shippingCost) {
    deviations.push({
      kind: 'shipping',
      lineId: null,
      label: 'Versandkosten',
      previousValue: `${money(previous.shippingCost)} ${previous.currency}`,
      currentValue: `${money(current.shippingCost)} ${current.currency}`,
      material: true,
    });
  }

  const previousTotal = totalOf(previous);
  const currentTotal = totalOf(current);
  if (previousTotal !== currentTotal) {
    deviations.push({
      kind: 'total',
      lineId: null,
      label: 'Gesamtsumme',
      previousValue: `${money(previousTotal)} ${previous.currency}`,
      currentValue: `${money(currentTotal)} ${current.currency}`,
      material: exceedsTolerance(previousTotal, currentTotal, rule),
    });
  }

  return deviations;
}

export function totalOf(snapshot: PriceSnapshot): number {
  const net = snapshot.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  return Math.round((net + snapshot.shippingCost) * 100) / 100;
}

export function hasMaterialDeviation(deviations: readonly PriceDeviation[]): boolean {
  return deviations.some((deviation) => deviation.material);
}
